// PUBLIC VIDEO WATCH PAGE - ISOLATED FROM AUTH SYSTEM
// Simple player for non-tech-savvy family members

import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { Play, Clock, Eye, AlertCircle } from 'lucide-react'

const PublicVideoWatch = () => {
  const { token } = useParams()
  const [video, setVideo] = useState(null)
  const [linkInfo, setLinkInfo] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (token) {
      fetchVideoData()
    }
  }, [token])

  const fetchVideoData = async () => {
    try {
      setLoading(true)
      const response = await fetch(`/.netlify/functions/public-video-access?token=${token}`)

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to load video')
      }

      const data = await response.json()

      // Get a working video URL using the same method as authenticated videos
      if (data.video && data.video.cloudflare_uid) {
        try {
          const urlResponse = await fetch('/.netlify/functions/public-r2-url', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              objectKey: `videos/${data.video.cloudflare_uid}/${data.video.filename}`,
              token: token
            })
          })

          if (urlResponse.ok) {
            const urlData = await urlResponse.json()
            data.video.video_url = urlData.downloadUrl
          }
        } catch (urlError) {
          console.error('Failed to get video URL:', urlError)
        }
      }

      setVideo(data.video)
      setLinkInfo(data.link)

    } catch (err) {
      console.error('Failed to fetch video:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const formatTimeRemaining = (expiresAt) => {
    const now = new Date()
    const expires = new Date(expiresAt)
    const diffMs = expires - now

    if (diffMs <= 0) return 'Expired'

    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24))
    const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))

    if (days > 0) return `${days} day${days > 1 ? 's' : ''} remaining`
    if (hours > 0) return `${hours} hour${hours > 1 ? 's' : ''} remaining`
    return 'Less than 1 hour remaining'
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-3 border-orange-500 border-t-transparent mx-auto mb-4"></div>
          <p className="text-white text-lg">Loading video...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-gray-800 rounded-lg p-6 text-center">
          <AlertCircle className="w-16 h-16 mx-auto mb-4 text-red-500" />
          <h1 className="text-xl font-bold text-white mb-2">Video Unavailable</h1>
          <p className="text-gray-300 mb-4">{error}</p>

          {error.includes('expired') && (
            <p className="text-sm text-gray-400">
              This video link has expired. Please ask for a new link.
            </p>
          )}

          {error.includes('limit reached') && (
            <p className="text-sm text-gray-400">
              This video has reached its view limit. Please ask for a new link.
            </p>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Simple header */}
      <header className="bg-gray-800 border-b border-gray-700 p-4">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-white text-xl font-semibold">Family Video</h1>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-4">
        {/* Video player */}
        <div className="bg-black rounded-lg overflow-hidden mb-6">
          {token ? (
            <video
              controls
              autoPlay
              className="w-full aspect-video"
              style={{ maxHeight: '70vh' }}
            >
              <source src={video?.video_url} type={video?.mime_type || 'video/mp4'} />
              <p className="text-white p-4">
                Your browser doesn't support video playback.
                <a href={`/.netlify/functions/public-video-access?token=${token}`} className="text-orange-500 underline ml-1">
                  Download the video instead
                </a>
              </p>
            </video>
          ) : (
            <div className="aspect-video flex items-center justify-center">
              <div className="text-center text-gray-400">
                <Play className="w-16 h-16 mx-auto mb-4" />
                <p>Video not available for playback</p>
              </div>
            </div>
          )}
        </div>

        {/* Video info */}
        <div className="bg-gray-800 rounded-lg p-6">
          <h2 className="text-2xl font-bold text-white mb-4">{video?.title}</h2>

          {/* Link info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="flex items-center space-x-2 text-gray-300">
              <Clock className="w-4 h-4" />
              <span>{formatTimeRemaining(linkInfo?.expires_at)}</span>
            </div>

            <div className="flex items-center space-x-2 text-gray-300">
              <Eye className="w-4 h-4" />
              <span>{linkInfo?.views_remaining} views left</span>
            </div>
          </div>

          {/* Simple instructions */}
          <div className="mt-6 p-4 bg-gray-700 rounded-lg">
            <h3 className="text-white font-medium mb-2">How to watch:</h3>
            <ul className="text-gray-300 text-sm space-y-1">
              <li>• Click the play button to start the video</li>
              <li>• Use the controls to pause, adjust volume, or go fullscreen</li>
              <li>• This link will expire automatically for security</li>
            </ul>
          </div>
        </div>
      </main>

      {/* Simple footer */}
      <footer className="mt-12 py-6 text-center text-gray-500 text-sm">
        <p>Private family video - Shared with love ❤️</p>
      </footer>
    </div>
  )
}

export default PublicVideoWatch